import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Science, OpenInNew} from '@mui/icons-material';
import { Tabs, Tab, Box } from '@mui/material';

// Custom
import MapCardActionButton from '@/ui/MapCard/MapCardActionButton';
import DataController from '@/lib/DataController';
import modelChemCompound from '@/models/chem_compound';
import modelChemElement from '@/models/chem_element';
import modelRadionucleids from '@/models/radionucleids';

import useApi from '@/lib/api/useApi';
import ModelTable from '@/ui/Table/ModelTable';


// Types
import { TCustomGFIDKPProps } from '@/@types/views/map';
import { DCRecord } from '@/@types/lib/dataController';


type TCompositionRecords = {
  compounds?: DCRecord[];
  rare_earth_elements?: DCRecord[];
  non_rare_earth_elements?: DCRecord[];
  radionucleids?: DCRecord[];
};

const CompositionTabBody = ({ compositionRecords }: { compositionRecords: TCompositionRecords | null }) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);

  const dcChemElement = new DataController(modelChemElement);
  const dcChemCompound = new DataController(modelChemCompound);
  const dcRadionucleids = new DataController(modelRadionucleids);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (compositionRecords) {
      if (compositionRecords.compounds?.length) {
        setTabIndex(0);
      } else if (compositionRecords.rare_earth_elements?.length) {
        setTabIndex(1);
      } else if (compositionRecords.non_rare_earth_elements?.length) {
        setTabIndex(2);
      } else if (compositionRecords.radionucleids?.length) {
        setTabIndex(3);
      }
    }
  }, [compositionRecords]);

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth">
        <Tab label={t('chem:tabs.composition.compounds')} disabled={!compositionRecords?.compounds?.length} />
        <Tab label={t('chem:tabs.composition.ree')} disabled={!compositionRecords?.rare_earth_elements?.length} />
        <Tab label={t('chem:tabs.composition.non_ree')} disabled={!compositionRecords?.non_rare_earth_elements?.length} />
        <Tab label={t('chem:tabs.composition.radionucleids')} disabled={!compositionRecords?.radionucleids?.length} />
      </Tabs>

      <Box role="tabpanel" hidden={tabIndex !== 0}>
        <ModelTable
          allowExport
          allowFilter
          title=""
          size="small"
          tableName="compounds"
          viewName="default"
          records={compositionRecords?.compounds || []}
          dc={dcChemCompound}
          allowSelection="none"
          allowAdd={false}
          allowRowAction={() => false}
          onRowAction={() => {}}
          disableControls={false}
          allowColumnPicker
        />
      </Box>

      <Box role="tabpanel" hidden={tabIndex !== 1}>
        <ModelTable
          allowExport
          allowFilter
          title=""
          size="small"
          tableName="ree"
          viewName="default"
          records={compositionRecords?.rare_earth_elements || []}
          dc={dcChemElement}
          allowSelection="none"
          allowAdd={false}
          allowRowAction={() => false}
          onRowAction={() => {}}
          disableControls={false}
          allowColumnPicker
        />
      </Box>

      <Box role="tabpanel" hidden={tabIndex !== 2}>
        <ModelTable
          allowExport
          allowFilter
          title=""
          size="small"
          tableName="non_ree"
          viewName="default"
          records={compositionRecords?.non_rare_earth_elements || []}
          dc={dcChemElement}
          allowSelection="none"
          allowAdd={false}
          allowRowAction={() => false}
          onRowAction={() => {}}
          disableControls={false}
          allowColumnPicker
        />
      </Box>

      <Box role="tabpanel" hidden={tabIndex !== 3}>
        <ModelTable
          allowExport
          allowFilter
          title=""
          size="small"
          tableName="radionucleids"
          viewName="default"
          records={compositionRecords?.radionucleids || []}
          dc={dcRadionucleids}
          allowSelection="none"
          allowAdd={false}
          allowRowAction={() => false}
          onRowAction={() => {}}
          disableControls={false}
          allowColumnPicker
        />
      </Box>
    </Box>
  );
};

const FICLocationCompositionButton = (props: TCustomGFIDKPProps) => {
  const identifier = 'composition';

  const [compositionRecords, setCompositionRecords] = useState<TCompositionRecords | null>(null);

  const api = useApi();

  const { t } = useTranslation();

  const { featureData, record, onToggle, addToCache, getFromCache } = props;

  const { layerData } = featureData;
  const { layer } = layerData;

  const recordId = useMemo(() => record ? (record.id ? record.id : record.fid ? record.fid : null) : null, [record]);

  const layerTag = useMemo(() => {
    const layerNameParts = layer.split('_', 2);
    return layerNameParts.length === 2 ? layerNameParts[1].toLowerCase() : layerNameParts[0].toLowerCase();
  }, [layer]);

  const loadData = useCallback(() => {
    if (record) {
      const cachedRecords = getFromCache(identifier, record.id as number);
      if (cachedRecords) {
        setCompositionRecords(cachedRecords as TCompositionRecords);
      } else {
        api.get(`chem/fic-lokacije/${record.id}/kompozicija`).then((resp) => {
          if (resp.success) {
            setCompositionRecords(resp.data as TCompositionRecords);
            addToCache(identifier, record.id as number, resp.data as DCRecord);
          }
        });
      }
    }
  }, [record]);

  useEffect(() => {
    if (record && record.id) {
      setCompositionRecords(null);
      loadData();
    }
  }, [recordId, layerTag, loadData]);

  const handleShowComposition = () => {
    onToggle(<CompositionTabBody compositionRecords={compositionRecords} />, identifier, true);
  };

  const buttonDisabled = !compositionRecords || 
    (!compositionRecords.compounds 
      && !compositionRecords.rare_earth_elements
      && !compositionRecords.non_rare_earth_elements
      && !compositionRecords.radionucleids);

  return (
    <MapCardActionButton
      startIcon={<Science />}
      onClick={handleShowComposition}
      disabled={buttonDisabled}
      endIcon={<OpenInNew />}
    >
      {t('map:buttons.composition')}
    </MapCardActionButton>
  );
};

export default FICLocationCompositionButton;
